import React, { useMemo } from 'react';
import SkeletonLoadingContainer from '../../../v2BaseComponents/SkeletonLoadingContainer';
import Text from '../../../v2BaseComponents/Text';
import PressableDynamicOpacity from '../../../v2BaseComponents/PressableDynamicOpacity';
import Icon, { IconName } from '../../../v2BaseComponents/Icon';
import Stack from '../../../v2BaseComponents/Stack';
import _truncate from 'lodash/truncate';
import { hexToRgbA } from '../../../../utils/hexToRGBA';
import { colors } from '../../../../utils/theme';
import { View } from 'react-native';

export type BaseSearchResultOwnProps = {
    onPress: () => void;
    text: string | null;
    secondaryHeader?: string;
    isLoading: boolean;
    iconName: IconName;
    subText?: string;
    placeholderLength?: number;
    searchText: string;
};

export type BaseSearchResultProps = BaseSearchResultOwnProps;

type MaybeHighlightedTextChunk = {
    text: string;
    isHighlighted: boolean;
};

interface Match {
    index: number;
    length: number;
}

interface MatchWindow {
    start: number;
    end: number;
    matchCount: number;
}

interface HighlightOptions {
    maxLength?: number;
}

function findBestWindow(content: string, matches: Match[], maxLength: number): MatchWindow {
    if (matches.length === 0) {
        return { start: 0, end: maxLength, matchCount: 0 };
    }

    let left = 0;
    let right = 0;
    let maxMatchCount = 0;
    let bestMatchRange = { start: left, end: right };

    while (right < matches.length) {
        while (
            right < matches.length &&
            matches[right].index + matches[right].length - matches[left].index <= maxLength
        ) {
            right++;
        }

        if (right - left > maxMatchCount) {
            maxMatchCount = right - left;
            bestMatchRange = { start: left, end: right };
        }

        left++;
    }

    const matchesStart = matches[bestMatchRange.start].index;
    const matchesEnd = matches[bestMatchRange.end - 1].index + matches[bestMatchRange.end - 1].length;
    const center = matchesStart + (matchesEnd - matchesStart) / 2;
    const halfWindow = Math.floor(maxLength / 2);

    let start = Math.max(0, Math.floor(center - halfWindow));
    let end = Math.min(content.length, Math.floor(center + halfWindow));

    // Adjust to not break words
    const adjustedStart = content.lastIndexOf(' ', start) + 1;
    const adjustedEnd = content.indexOf(' ', end);

    start = adjustedStart >= 0 ? adjustedStart : 0;
    end = adjustedEnd >= 0 ? adjustedEnd : end;

    // If adjusting made our window too long, trim from the end at a word boundary
    if (end - start > maxLength) {
        const lastSpace = content.lastIndexOf(' ', start + maxLength);
        end = lastSpace > start ? lastSpace : start + maxLength;
    }

    return { start, end, matchCount: maxMatchCount };
}

function getRelevantHighlightedText(
    searchText: string,
    content: string,
    options: HighlightOptions = {},
): MaybeHighlightedTextChunk[] {
    const maxLength = options.maxLength ?? 140;

    if (!searchText.trim() || !content.trim()) {
        return [{ text: content.slice(0, maxLength), isHighlighted: false }];
    }

    const escapeRegex = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const searchTerms = searchText.toLowerCase().split(/\s+/).filter(Boolean).map(escapeRegex);
    // nosemgrep
    const searchRegex = new RegExp(`(${searchTerms.join('|')})`, 'gi');
    const matches: Match[] = [];

    let match;
    while ((match = searchRegex.exec(content)) !== null) {
        matches.push({ index: match.index, length: match[0].length });
    }

    const window = findBestWindow(content, matches, maxLength);

    if (window.matchCount === 0) {
        return [{ text: content.slice(0, maxLength), isHighlighted: false }];
    }

    const result: MaybeHighlightedTextChunk[] = [];
    let currentIndex = window.start;

    if (window.start > 0) {
        result.push({ text: '...', isHighlighted: false });
    }

    matches
        .filter((m) => m.index >= window.start && m.index + m.length <= window.end)
        .forEach((match) => {
            if (currentIndex < match.index) {
                result.push({
                    text: content.slice(currentIndex, match.index),
                    isHighlighted: false,
                });
            }
            result.push({
                text: content.slice(match.index, match.index + match.length),
                isHighlighted: true,
            });
            currentIndex = match.index + match.length;
        });

    if (currentIndex < window.end) {
        result.push({
            text: content.slice(currentIndex, window.end),
            isHighlighted: false,
        });
    }

    if (window.end < content.length) {
        result.push({ text: '...', isHighlighted: false });
    }

    return result;
}

const BaseSearchResult: React.FC<BaseSearchResultProps> = ({
    onPress,
    text,
    isLoading,
    iconName,
    subText,
    placeholderLength,
    secondaryHeader,
    searchText,
}) => {
    const textMatch = useMemo(() => {
        if (!text) return [];

        return getRelevantHighlightedText(searchText, text);
    }, [text, searchText]);

    const subTextMatch = useMemo(() => {
        if (!subText) return [];

        return getRelevantHighlightedText(searchText, subText);
    }, [subText, searchText]);

    if (!text && !isLoading) return null;

    const placeholderText = placeholderLength ? 'x'.repeat(placeholderLength) : 'Placeholder Text Placeholder';

    return (
        <View style={{ flex: 1, alignSelf: 'stretch', padding: 4 }}>
            <PressableDynamicOpacity
                onPress={onPress}
                testID="profile-search-result"
                style={{ alignSelf: 'stretch', alignItems: 'center', flex: 1, borderRadius: 4 }}
                addHoverEffect
                customStateStyles={{
                    hovered: {
                        backgroundColor: hexToRgbA(colors['Grey-Light'], 0.2),
                    },
                    pressed: {
                        backgroundColor: hexToRgbA(colors['Grey-Light'], 0.5),
                    },
                }}
                hoverDetectionContainerStyles={{
                    flex: 1,
                    alignSelf: 'stretch',
                }}
            >
                <Stack
                    horizontal
                    justify="flex-start"
                    space={2}
                    style={{ flex: 1, alignSelf: 'stretch', padding: 8 }}
                    align="center"
                >
                    <Icon size="profile-search-result-icon" name={iconName} colorVariant="Primary" />
                    <SkeletonLoadingContainer
                        isLoading={isLoading}
                        skeletonStyle={{
                            alignSelf: 'stretch',
                            flex: 1,
                        }}
                        loadingSkeletonStyle={{
                            height: 28,
                        }}
                    >
                        <Stack vertical style={{ flex: 1 }}>
                            <Stack horizontal space={1} style={{ alignSelf: 'stretch' }}>
                                <Text variant="bold-h5" wrapText style={{ flex: 1 }}>
                                    {isLoading
                                        ? placeholderText
                                        : textMatch.map((chunk, index) => (
                                              <Text
                                                  key={index + chunk.text}
                                                  variant={chunk.isHighlighted ? 'bold-h5' : 'h5'}
                                                  color={chunk.isHighlighted ? 'Dark-Text' : 'Gray-Text'}
                                              >
                                                  {chunk.text}
                                              </Text>
                                          ))}
                                    {secondaryHeader ? (
                                        <Text variant="caption-1" color="Gray-Text" style={{ lineHeight: 24 }}>
                                            {` (${secondaryHeader})`}
                                        </Text>
                                    ) : null}
                                </Text>
                            </Stack>
                            {subText ? (
                                <Text variant="caption-1" color="Gray-Text">
                                    {subTextMatch.map((chunk, index) => (
                                        <Text
                                            key={index + chunk.text}
                                            variant={chunk.isHighlighted ? 'bold-caption-1' : 'caption-1'}
                                            color={chunk.isHighlighted ? 'Dark-Text' : 'Gray-Text'}
                                        >
                                            {chunk.text}
                                        </Text>
                                    )) || placeholderText}
                                </Text>
                            ) : null}
                        </Stack>
                    </SkeletonLoadingContainer>
                </Stack>
            </PressableDynamicOpacity>
        </View>
    );
};

export default React.memo(BaseSearchResult);
